import type { RequestConfig } from 'umi';
import type { I } from '@/types/project';
import '@/page-size-watcher';
import * as apis from '@/apis/apis';
import * as apiss from '@/pages/home/apis';
import { getEpAuthQuery } from '@/apis/eoc-soa-api';
import { errorAdaptor } from '@/apis/handler';
import { requestInterceptors, responseInterceptors } from '@/apis/interceptors';
import { PageLoading } from '@ant-design/pro-layout';
import '@/styles/global.less';
import { log } from '@/utils/log';
import probes from '@hll/sec-sign-sdk';
// utils
import stat from '@/utils/stat';
import { setWatchdogBizUserId } from './utils/watch-dog';
import traceIdProbes from '@hll/trace-id-sdk';
import { getQueryString } from '@hll/eapp-utils';
import { isStaticPage } from '@/utils/static';
import { skeletonUtils } from '@hll/skeleton-webpack-plugin';

const env = (document.documentElement.dataset.vanEnv as I.Env) || 'dev';

const SIGN_DEBUG_KEY = 'SIGN_DEBUG';
const signDebug =
  sessionStorage.getItem(SIGN_DEBUG_KEY) || getQueryString(SIGN_DEBUG_KEY);
// 签名sdk
probes.init({
  debug: signDebug === '1',
  allowList: ['eapi.huolala.cn', 'eapi-stg.huolala.cn', 'eapi-pre.huolala.cn'],
});

// 定义 request 对象 https://umijs.org/zh-CN/plugins/plugin-request
export const request: RequestConfig = {
  credentials: 'include', // 是否携带 cookie
  timeout: 20000, // 超时设置
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded',
  },
  // 错误适配：是否报错、错误信息格式组装
  errorConfig: {
    adaptor: errorAdaptor(),
  },
  requestInterceptors,
  responseInterceptors,
};

function initTranceIdProbes() {
  try {
    traceIdProbes.init({
      allowList: [
        'eapi.huolala.cn',
        'eapi-stg.huolala.cn',
        'eapi-pre.huolala.cn',
      ],
      openProxyLocal: true,
    });
  } catch (e) {
    log.error('initTranceIdProbes error');
  }
}

// App 初始化时的数据
// 通常来说是获取当前用户信息，查询用户拥有的权限
export async function getInitialState(): Promise<I.InitialState> {
  let params = {
    env,
    role: '',
    roleName: '',
    userId: '',
    cityId: '',
    productService: ['1'],
    staffNum: '',
    staffName: '',
    epId: '',
    licenseNo: '',
    companyName: '',
    staffId: '',
    isEpNew: '',
    phoneNo: '',
    verify: '1',
    verifyDesc: '',
    departId: '',
    whiteList: [],
    isWhite: '',
    showCheckToolAuth: false,
    menuAuth: [],
    isInAuthGroup: false,
    departDisplay: 1,
    batchGrayList: undefined,
    userInfo: {},
    walletInfo: {},
    roleDesc: '',
    staffNo: '',
    staffEmail: '',
    departName: '',
    cityName: '',
    name: '',
    isSetQuota: '',
    quotaTotalFen: '',
    isSetOrderOuota: '',
    orderQuotaFen: '',
    isSetOrderQuota: '',
    epVersionAndPackage: undefined,
    welfareInfo: {},
    accountPeriod: {},
    epAuthData: {},
    hasOrderApproveEquity: false,
  };

  skeletonUtils.remove();

  // 静态、免登页面不请求接口
  if (isStaticPage()) {
    return params;
  }

  try {
    const promises = [
      apis.getUserInfo(),
      // 获取菜单是否展示优惠券包标签
      apis.getEpWelfareInfo(),
      // 获取版本信息&权益包
      apis.getEpVersionAndPackage({
        loginIntroduceAndModeSelect: 1,
      }),
      // 是否命中交易中高频异常企业
      getEpAuthQuery(),
      // 是否有下单审批权限
      apis.getOrderApproveAuth(),
      /* 注：暂时不要删 */
      // apis.getLicenseWhiteList(),
      // apis.getPhoneLicenseWhiteList(),
      apis.getCheckToolAuth(),
      // 临时用于判断是否展示月结账期，暂时这么处理，即将下线
      apiss.getWalletInfo(),
      apis.getUserMenuAuth(),
      // 批量获取灰度
      apis.getGrayScaleBatch([
        'pc_big_screen',
        'pc_big_screen_today_show',
        'pc_favorite_driver_list',
        'pc_ep_relation',
        'order_approve_config',
        'place_order_designated_driver',
        'pc_invoice',
        'orchestrator_wallet_detail_offline',
        'ep_consignee_order_msg_switch',// 通知收货人按钮是否展示
        'wallet_flow_special_ep_id', // 钱包余额流水新增白名单
        'calculate_place_order_form_to_json_gray', // 计价、下单表单转json灰度
        'show_receipt_addr', // 纸质回单是否展示回单送回方式和地址；
        'commodity_related_options_last_selected', // 商品附加选项（是否允许拼车、是否电仪）是否走本地缓存标识
      ]),
      // 账期信息
      apis.getAccountPeriodPageUrls(),
    ];
    const allResult = await Promise.allSettled(promises);
    const [
      userInfo,
      welfareInfo,
      epVersionInfo,
      epAuthData,
      orderApproveData,
      // epIds,
      // isPhoneInLicenseWhiteList,
      checkToolAuth,
      walletInfoData,
      menuAuth,
      batchGrayListRes,
      accountPeriodData
    ] = allResult.map((i) => {
      if (i.status === 'fulfilled') {
        return i.value;
      }
      return { data: {} };
    });

    const { switch: showCheckToolAuth } = checkToolAuth.data;
    const { grayscaleResult: batchGrayList } = batchGrayListRes.data;
    
    // 新增：存储灰度配置到 sessionStorage
    try {
      const shouldUseJsonHeader = !!batchGrayList?.calculate_place_order_form_to_json_gray;
      sessionStorage.setItem('calculate_place_order_form_to_json_gray', shouldUseJsonHeader.toString());
    } catch (error) {
      console.error('Failed to save gray scale config to sessionStorage:', error);
    }
    const {
      role,
      user_id: userId,
      user_fid: userFid,
      city_id: cityId,
      product_service: productService,
      staff_num: staffNum,
      staff_name: staffName,
      ep_id: epId,
      company_name: companyName,
      staff_id: staffId,
      is_ep_new: isEpNew,
      phone_no: phoneNo,
      depart_id: departId,
      verify,
      verify_desc: verifyDesc,
      license_no: licenseNo,
      role_desc: roleDesc,
      staff_no: staffNo,
      staff_email: staffEmail,
      depart_name: departName,
      city_name: cityName,
      name,
      is_set_quota: isSetQuota,
      quota_total_fen: quotaTotalFen,
      is_set_order_quota: isSetOrderQuota,
      order_quota_fen: orderQuotaFen,
    } = userInfo.data;

    log.log('用户信息', { userId, userFid, epId });

    if (userFid) {
      // 埋点登录
      stat?.login(userFid);
    }

    setWatchdogBizUserId(userId);
    initTranceIdProbes();
    // const { ep_id: whiteList } = epIds.data;
    // const { is_white: isWhite } = isPhoneInLicenseWhiteList.data;
    const tmpMenuAuth = Array.isArray(menuAuth.data) ? menuAuth.data : [];

    let roleName = '';
    switch (role) {
      case '1':
        roleName = '超管';
        break;
      case '2':
        roleName = '普通员工';
        break;
      case '3':
        roleName = '部门管理员';
        break;
      default:
        roleName = '超管';
    }

    params = {
      ...params,
      role,
      roleName,
      userId,
      cityId,
      productService,
      staffNum,
      staffName,
      epId,
      licenseNo,
      companyName,
      staffId,
      isEpNew,
      phoneNo,
      verify,
      verifyDesc,
      departId,
      // whiteList,
      // isWhite,
      showCheckToolAuth: showCheckToolAuth && role === '1',
      menuAuth: tmpMenuAuth,
      isInAuthGroup: tmpMenuAuth.length > 0,
      batchGrayList,
      userInfo: userInfo.data,
      walletInfo: walletInfoData?.data,
      accountPeriod: accountPeriodData?.data,
      roleDesc,
      staffNo,
      staffEmail,
      departName,
      cityName,
      name,
      isSetQuota,
      quotaTotalFen,
      isSetOrderQuota,
      orderQuotaFen,
      epVersionAndPackage: epVersionInfo?.data,
      welfareInfo: welfareInfo?.data,
      epAuthData: epAuthData?.data,
      hasOrderApproveEquity: !!orderApproveData?.data.orderApprove, // 0：否，1：是
    };

    if (epId) {
      stat.track('eppc_systemversion_expo', {
        version_type: '新版',
        ep_id: epId,
        user_id: userId,
        user_role: roleName,
      });
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    log.error(error);
  }

  return params;
}

// 页面初始化时的 loading
export const initialStateConfig = {
  loading: <PageLoading />,
};
